<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <!-- Cabecalho no padrao das telas da area do credor/procurador -->
    <header class="ac-header">
      <span class="ac-eyebrow">
        <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
        {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
      </span>
      <div class="ac-title-row">
        <h1 class="ac-title">Meus <span>credenciamentos</span></h1>
        <v-btn v-if="user" color="primary" depressed class="ac-btn" :disabled="isProcurator && !selectedCreditorId" @click="createAccreditation()">
          <v-icon left small>mdi-plus</v-icon> Novo credenciamento
        </v-btn>
      </div>
      <p class="ac-subtitle">Pedidos para participar das assembleias de credores, com a situação de cada um.</p>
    </header>

    <template v-if="user">
      <template v-if="isProcurator">
        <div class="ac-notice">
          <v-icon small color="#7e55ab">mdi-information-outline</v-icon>
          <div>Todos os credores representados aparecem abaixo, agrupados por caso. Selecione um credor para filtrar ou criar um credenciamento.</div>
        </div>
        <div class="ac-field">
          <procurator-creditor-selector :required="false" v-model="selectedCreditorId"></procurator-creditor-selector>
        </div>
      </template>

      <v-progress-linear v-if="loading" indeterminate color="darkSecondary" class="mb-3"></v-progress-linear>

      <div v-if="!loading && !items.length" class="ac-empty">
        <div class="ac-empty__icon"><v-icon size="34" color="darkSecondary">mdi-account-check-outline</v-icon></div>
        <div class="ac-empty__title">Nenhum credenciamento encontrado</div>
        <div class="ac-empty__text">Solicite o credenciamento para participar e votar nas assembleias de credores.</div>
      </div>

      <section v-for="group in groups" :key="group.name" class="ac-group">
        <button type="button" class="ac-group__header" @click="toggleGroup(group.name)">
          <v-icon small class="ac-group__chevron" :class="{ 'ac-group__chevron--closed': collapsed[group.name] }">mdi-chevron-down</v-icon>
          <span class="ac-group__name">{{ group.name }}</span>
          <span class="ac-group__count">{{ group.items.length }} {{ group.items.length === 1 ? "credenciamento" : "credenciamentos" }}</span>
        </button>
        <v-expand-transition>
          <div v-show="!collapsed[group.name]">
            <article v-for="item in group.items" :key="item.id" class="ac-row">
              <div class="ac-row__icon"><v-icon small>mdi-account-group-outline</v-icon></div>
              <div class="ac-row__info">
                <div class="ac-row__title">{{ item.generalMeeting ? item.generalMeeting.name : "Assembleia" }}</div>
                <div class="ac-muted">
                  <span v-if="item.accreditation && item.accreditation.user" class="ac-capitalize">{{ item.accreditation.user.name }} · </span>
                  {{ getParticipationType(item.participation_type) }} · solicitado em {{ item.created_at | defaultDate('DD/MM/YYYY HH:mm [BRT]') }}
                  <span v-if="item.regularized_at"> · complementado em {{ item.regularized_at | defaultDate('DD/MM/YYYY HH:mm [BRT]') }}</span>
                </div>
                <!-- O que a AJ pediu: o complemento e feito neste mesmo pedido em "Regularizar" -->
                <div v-if="[2, 3].includes(Number(item.status)) && item.reason" class="ac-reason" :class="{ 'ac-reason--regularize': Number(item.status) === 3 }">
                  <strong>{{ Number(item.status) === 3 ? 'O que regularizar:' : 'Motivo:' }}</strong>
                  <span class="ml-1" v-html="item.reason"></span>
                </div>
              </div>
              <span class="ac-pill" :class="`ac-pill--${statusTone(item.status)}`"><span class="ac-pill__dot"></span>{{ getStatusText(item.status) }}</span>
              <div class="ac-row__actions">
                <v-btn v-if="canRegularize(item)" text rounded small color="#a16207" class="ac-action" @click="regularizeAccreditation(item)">
                  <v-icon left small>mdi-file-document-refresh-outline</v-icon> Regularizar
                </v-btn>
                <v-btn v-if="canEditProcurator(item)" text rounded small color="darkSecondary" class="ac-action" @click="openProcuratorDialog(item)">
                  <v-icon left small>mdi-account-edit-outline</v-icon> Procurador
                </v-btn>
                <v-btn v-if="canRequestSubstitution(item)" text rounded small color="darkSecondary" class="ac-action" @click="requestSubstitution(item)">
                  <v-icon left small>mdi-account-arrow-right-outline</v-icon> Substabelecer
                </v-btn>
                <v-btn v-if="canRemove(item)" icon small :loading="removingId === item.id" :disabled="removingId === item.id"
                  title="Remover credenciamento pendente" aria-label="Remover credenciamento pendente" @click="removeAccreditation(item)">
                  <v-icon small color="#b42318">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </article>
          </div>
        </v-expand-transition>
      </section>

      <div v-if="total" class="ac-footer">
        <span class="ac-muted">{{ rangeLabel }}</span>
        <v-pagination v-if="pageCount > 1" v-model="options.page" :length="pageCount" :total-visible="7" circle color="primary" />
      </div>

      <!-- Mensagens da AJ sobre credenciamentos removidos, apresentadas como atualizacoes.
           So aparece quando existe alguma; nao menciona exclusao nem envia e-mail. -->
      <section v-if="deletedItems.length" class="ac-updates">
        <div class="ac-list-head"><h2>Comunicados da Administração Judicial</h2><span>{{ deletedItems.length }}</span></div>
        <div v-for="item in deletedItems" :key="`update-${item.id}`" class="ac-update">
          <div class="ac-update__title">{{ item.general_meeting_name || 'Assembleia' }} <span v-if="item.client_name" class="ac-muted">· {{ item.client_name }}</span></div>
          <div class="ac-muted">{{ item.deleted_at | defaultDate('DD/MM/YYYY HH:mm [BRT]') }}</div>
          <div class="ac-update__message mt-2" v-html="item.deletion_reason"></div>
        </div>
      </section>
    </template>

    <login-required-notice v-else />

	    <v-dialog v-model="procuratorDialog.show" max-width="620" persistent>
	      <v-card class="ac-dialog">
	        <div class="ac-dialog__head">
	          <div><span class="ac-eyebrow">CREDENCIAMENTO</span><h2 class="ac-dialog__title">Procurador do credenciamento</h2></div>
	          <v-btn icon aria-label="Fechar" :disabled="procuratorDialog.loading" @click="closeProcuratorDialog"><v-icon>mdi-close</v-icon></v-btn>
	        </div>
	        <v-card-text class="ac-dialog__body ac-form">
	          <div class="ac-notice ac-notice--tight">
	            <v-icon small color="#7e55ab">mdi-information-outline</v-icon>
	            <div>{{ procuratorDialog.item && Number(procuratorDialog.item.status) === 1
	              ? 'Ao alterar o procurador, o credenciamento aprovado voltará para análise.'
	              : 'Esta alteração fica disponível enquanto o credenciamento estiver pendente.' }}</div>
	          </div>
	          <v-text-field
	            v-model="procuratorDialog.form.procurator_name"
	            label="Nome do procurador"
	            outlined
	            dense
	            :rules="[$rules.required]"
	          ></v-text-field>
		          <v-text-field
		            v-model="procuratorDialog.form.procurator_email"
	            label="E-mail do procurador"
	            outlined
	            dense
		            :rules="[$rules.required, $rules.email]"
		          ></v-text-field>
		          <v-text-field
		            v-model="procuratorDialog.form.procurator_registration"
		            label="OAB (se advogado)"
		            outlined
		            dense
		            :rules="procuratorDialog.form.procurator_oab_state ? [$rules.required] : []"
		          ></v-text-field>
		          <v-select
		            v-model="procuratorDialog.form.procurator_oab_state"
		            :items="statesList"
		            item-text="name"
		            item-value="name"
		            label="UF da OAB"
		            outlined
		            dense
		            :rules="procuratorDialog.form.procurator_registration ? [$rules.required] : []"
		          ></v-select>
			          <v-checkbox
			            color="darkSecondary"
			            v-model="procuratorDialog.form.use_same_procuration"
			            label="Usar a mesma procuração"
			            dense
			            hide-details
			          ></v-checkbox>
			          <v-file-input
			            v-if="!procuratorDialog.form.use_same_procuration"
			            v-model="procuratorDialog.form.procuration_file"
			            label="Nova procuração"
			            accept="image/png, image/jpeg, application/pdf"
			            outlined
			            dense
			            show-size
			            prepend-icon=""
			            class="mt-3"
			          ></v-file-input>
	        </v-card-text>
	        <v-card-actions class="ac-dialog__footer">
	          <v-spacer></v-spacer>
	          <v-btn text class="ac-action" @click="closeProcuratorDialog" :disabled="procuratorDialog.loading">Cancelar</v-btn>
	          <v-btn depressed color="primary" class="ac-btn" @click="saveProcurator" :loading="procuratorDialog.loading">
	            Salvar
	          </v-btn>
	        </v-card-actions>
	      </v-card>
	    </v-dialog>
	  </section>
	</template>

<script>
import axios from 'axios';
import ProcuratorCreditorSelector from '@/components/ProcuratorCreditorSelector.vue';
import statesList from '@/common/data/states-list.data';

export default {
  name: 'AccreditationsList',

  components: {
    ProcuratorCreditorSelector,
  },

  data() {
    return {
		      user: null,
	        statesList,
        selectedCreditorId: null,
        // Credenciamentos excluidos pela Administracao Judicial, com o motivo.
        deletedItems: [],
	      loading: false,
	      removingId: null,
	      procuratorDialog: {
	        show: false,
	        loading: false,
	        item: null,
		        form: {
		          procurator_name: '',
		          procurator_email: '',
			          procurator_registration: '',
			          procurator_oab_state: '',
			          use_same_procuration: true,
			          procuration_file: null,
			        },
	      },
	      items: [],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      // Casos recolhidos na lista (por nome do caso).
      collapsed: {},
    };
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.loadAccreditations();
      },
    },
    selectedCreditorId() {
      this.options.page = 1;
      this.loadAccreditations();
    },
  },

  computed: {
    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },
    // Itens da pagina agrupados por caso, na ordem em que chegam.
    groups() {
      const groups = new Map();
      for (const item of this.items) {
        const name = item.client_group || "Sem caso";
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(item);
      }
      return [...groups.entries()].map(([name, items]) => ({ name, items }));
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.options.itemsPerPage));
    },
    rangeLabel() {
      if (!this.total) return "";
      const start = (this.options.page - 1) * this.options.itemsPerPage + 1;
      const end = Math.min(this.total, start + this.items.length - 1);
      return `${start}–${end} de ${this.total} ${this.total === 1 ? "credenciamento" : "credenciamentos"}`;
    },
  },

  created() {
    this.user = this.$root.user;
    // Sem a tabela, a primeira carga sai daqui (antes vinha do options.sync).
    if (this.user) this.loadAccreditations();
  },

  methods: {
    async loadAccreditations() {

      this.loading = true;

      try {
        const result = await axios.get('/api/accreditations', {
          params: {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            creditor_user_id: this.selectedCreditorId || undefined,
          },
        });

        this.items = result.data.content.data.map(item => ({ ...item, client_group: this.getClientName(item) }));
        this.total = result.data.content.paging.total;

        await this.loadDeletedAccreditations();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      } finally {
        this.loading = false;
      }
    },

    async loadDeletedAccreditations() {
      try {
        const result = await axios.get('/api/accreditations/deleted', {
          params: { creditor_user_id: this.selectedCreditorId || undefined },
        });
        this.deletedItems = Array.isArray(result.data.content) ? result.data.content : [];
      } catch (error) {
        this.deletedItems = [];
      }
    },

    createAccreditation(generalMeetingId, regularizeId, creditorUserId) {
      const route = { path: '/assembleia-credores/credenciamento' };

      if (generalMeetingId) route.query = { id: generalMeetingId };
      if (regularizeId) route.query = { ...(route.query || {}), regularize_id: regularizeId };
      if (creditorUserId || this.selectedCreditorId) route.query = { ...(route.query || {}), creditor_user_id: creditorUserId || this.selectedCreditorId };

      this.$router.push(route);
    },

	    canRemove(item) {
	      return Number(item.status) === 0;
	    },

	    canRegularize(item) {
	      return [2, 3].includes(Number(item.status)) && item.generalMeeting && Number(item.generalMeeting.status) === 0;
	    },

	    regularizeAccreditation(item) {
	      this.createAccreditation(item.generalMeeting.id, item.id, item.accreditation && item.accreditation.user_id);
	    },

		    canEditProcurator(item) {
		      return Number(item.status) === 0;
		    },

		    canRequestSubstitution(item) {
		      return Number(item.status) === 1 && Number(item.participation_type || 1) === 1;
		    },

		    requestSubstitution(item) {
		      this.$router.push({ path: '/substabelecimentos', query: { accreditation_id: item.id } });
		    },

	    openProcuratorDialog(item) {
	      this.procuratorDialog.item = item;
	      this.procuratorDialog.form = {
		        procurator_name: item.procurator_name || '',
		        procurator_email: item.procurator_email || '',
			        procurator_registration: item.procurator_registration || '',
			        procurator_oab_state: item.procurator_oab_state || '',
			        use_same_procuration: true,
			        procuration_file: null,
			      };
	      this.procuratorDialog.show = true;
	    },

	    closeProcuratorDialog() {
	      if (this.procuratorDialog.loading) return;

	      this.procuratorDialog.show = false;
	      this.procuratorDialog.item = null;
	      this.procuratorDialog.form = {
		        procurator_name: '',
		        procurator_email: '',
			        procurator_registration: '',
			        procurator_oab_state: '',
			        use_same_procuration: true,
			        procuration_file: null,
			      };
	    },

	    async saveProcurator() {
	      if (!this.procuratorDialog.form.procurator_name) {
	        this.$dialog.notify.error('Informe o nome do procurador.', { position: 'top-right', timeout: 3000 });
	        return;
	      }

		      if (!this.procuratorDialog.form.procurator_email) {
		        this.$dialog.notify.error('Informe o e-mail do procurador.', { position: 'top-right', timeout: 3000 });
		        return;
		      }

			      if (!!this.procuratorDialog.form.procurator_registration !== !!this.procuratorDialog.form.procurator_oab_state) {
			        this.$dialog.notify.error('Ao informar a OAB, preencha também a respectiva UF.', { position: 'top-right', timeout: 3000 });
			        return;
			      }

			      if (!this.procuratorDialog.form.use_same_procuration && !this.procuratorDialog.form.procuration_file) {
			        this.$dialog.notify.error('Informe a nova procuração.', { position: 'top-right', timeout: 3000 });
			        return;
			      }

	      this.procuratorDialog.loading = true;

	      try {
	        const payload = new FormData();
	        payload.append('procurator_name', this.procuratorDialog.form.procurator_name);
	        payload.append('procurator_email', this.procuratorDialog.form.procurator_email);
	        payload.append('procurator_registration', this.procuratorDialog.form.procurator_registration || '');
	        payload.append('procurator_oab_state', this.procuratorDialog.form.procurator_oab_state || '');
	        payload.append('use_same_procuration', this.procuratorDialog.form.use_same_procuration ? '1' : '0');

	        if (!this.procuratorDialog.form.use_same_procuration) {
	          payload.append('procuration_file', this.procuratorDialog.form.procuration_file);
	        }

	        await axios.patch(`/api/accreditations/${this.procuratorDialog.item.id}/procurator`, payload);
	        const message = Number(this.procuratorDialog.item.status) === 1
	          ? 'Procurador salvo com sucesso. O credenciamento voltou para análise.'
	          : 'Procurador salvo com sucesso.';
	        this.$dialog.notify.info(message, { position: 'top-right', timeout: 3000 });
	        this.procuratorDialog.show = false;
	        this.procuratorDialog.item = null;
	        await this.loadAccreditations();
	      } catch (error) {
	        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
	      } finally {
	        this.procuratorDialog.loading = false;
	      }
	    },
	
	    async removeAccreditation(item) {
      const proceedAction = await this.$dialog.confirm({
        text: 'Deseja remover este credenciamento pendente?',
        title: 'Remover credenciamento',
        actions: {
          false: 'Cancelar',
          true: 'Remover'
        }
      });

      if (!proceedAction) return;

      this.removingId = item.id;

      try {
        await axios.delete(`/api/accreditations/${item.id}`);
        this.$dialog.notify.info('Credenciamento removido com sucesso.', { position: 'top-right', timeout: 3000 });
        await this.loadAccreditations();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
      } finally {
        this.removingId = null;
      }
    },

    getClientName(item) {
      if (item.generalMeeting && item.generalMeeting.client) return item.generalMeeting.client.name;
      if (item.accreditation && item.accreditation.client) return item.accreditation.client.name;
      return 'N/A';
    },

    getParticipationType(type) {
      return Number(type || 1) === 2 ? 'Ouvinte' : 'Credor';
    },

    getStatusText(status) {
      switch (Number(status)) {
        case 0: return 'Pendente';
        case 1: return 'Aprovado';
        case 2: return 'Rejeitado';
        case 3: return 'Regularização solicitada';
        default: return 'N/A';
      }
    },

    statusTone(status) {
      return { 0: 'warn', 1: 'ok', 2: 'danger', 3: 'regularize' }[Number(status)] || 'muted';
    },

    toggleGroup(name) {
      this.$set(this.collapsed, name, !this.collapsed[name]);
    },
  },
};
</script>

<style scoped>
/* Mesmo padrao visual das telas da area do credor/procurador. */
.ac-header { padding: 32px 0 20px; }
.ac-eyebrow { display: flex; gap: 8px; align-items: center; font-size: 11px; font-weight: 700; letter-spacing: .12em; color: #766386; }
.ac-title-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin: 14px 0 10px; }
.ac-title { margin: 0; font-size: clamp(28px, 3.4vw, 40px); font-weight: 700; color: #25113c; letter-spacing: -.035em; line-height: 1.2; }
.ac-title span { color: #806697; }
.ac-subtitle { margin: 0; color: #73677c; font-size: 14px; }
.ac-muted { font-size: 13px; color: rgba(24, 0, 50, .55); }
.ac-capitalize { text-transform: capitalize; }

.ac-btn { height: 40px !important; padding: 0 18px !important; border-radius: 10px; text-transform: none; letter-spacing: 0; font-weight: 600; }
.ac-action { text-transform: none; letter-spacing: normal; font-weight: 600; }

.ac-notice { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border-radius: 12px; background: rgba(120, 119, 229, .09); color: #2a1a40; font-size: 14px; line-height: 1.55; margin-bottom: 16px; }
.ac-notice .v-icon { margin-top: 2px; }
.ac-notice--tight { margin-bottom: 14px; }

.ac-field { max-width: 520px; }
.ac-field ::v-deep .v-text-field--outlined fieldset, .ac-form ::v-deep .v-text-field--outlined fieldset { border-color: rgba(126, 85, 171, .45); border-radius: 10px; }
.ac-field ::v-deep .v-text-field--outlined:hover fieldset, .ac-form ::v-deep .v-text-field--outlined:hover fieldset { border-color: #7e55ab; }
.ac-field ::v-deep .v-input__slot, .ac-form ::v-deep .v-input__slot { border-radius: 10px !important; }
.ac-field ::v-deep .v-alert { border-radius: 12px; }

/* Grupo por caso */
.ac-group { margin-bottom: 12px; }
.ac-group__header { display: flex; align-items: center; gap: 8px; width: 100%; border: 0; background: transparent; padding: 10px 4px; cursor: pointer; text-align: left; }
.ac-group__chevron { transition: transform .2s ease; }
.ac-group__chevron--closed { transform: rotate(-90deg); }
.ac-group__name { font-size: 13px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #7e55ab; }
.ac-group__count { font-size: 12px; color: rgba(24, 0, 50, .5); }

/* Linha do credenciamento */
.ac-row { display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto; align-items: center; gap: 16px; padding: 14px 18px; border-radius: 16px; background: #fff; border: 1px solid rgba(24, 0, 50, .07); margin-bottom: 8px; transition: border-color .15s ease, box-shadow .15s ease; }
.ac-row:hover { border-color: rgba(120, 119, 229, .35); box-shadow: 0 6px 20px rgba(24, 0, 50, .06); }
.ac-row__icon { width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(120, 119, 229, .12); color: #7e55ab; }
.ac-row__icon .v-icon { color: inherit; }
.ac-row__title { font-size: 15px; font-weight: 600; color: #180032; overflow-wrap: anywhere; }
.ac-row__actions { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 2px; }

.ac-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.ac-pill__dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.ac-pill--ok { background: #e7f6ee; color: #1e7b4f; }
.ac-pill--warn { background: #fff4dc; color: #a16207; }
.ac-pill--danger { background: #fdecea; color: #b42318; }
.ac-pill--muted { background: #f1f0f4; color: #6b6475; }
.ac-pill--regularize { background: #f1ebfb; color: #6d28d9; }
.ac-reason { margin-top: 6px; font-size: 13px; color: #b42318; }
.ac-reason--regularize { color: #6d28d9; }
.ac-reason ::v-deep p { display: inline; margin: 0; }

.ac-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-top: 12px; }

.ac-empty { text-align: center; padding: 40px 16px 56px; }
.ac-empty__icon { width: 72px; height: 72px; border-radius: 50%; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; background: rgba(120, 119, 229, .12); }
.ac-empty__title { font-size: 17px; font-weight: 600; color: #180032; }
.ac-empty__text { margin-top: 6px; color: rgba(24, 0, 50, .6); font-size: 14px; }

/* Comunicados do AJ */
.ac-updates { margin: 28px 0 40px; }
.ac-list-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 12px; }
.ac-list-head h2 { margin: 0; font-size: 13px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #7e55ab; }
.ac-list-head span { font-size: 12px; color: rgba(24, 0, 50, .5); }
.ac-update { padding: 14px 18px; border-radius: 14px; background: rgba(120, 119, 229, .07); margin-bottom: 8px; }
.ac-update__title { font-size: 14px; font-weight: 600; color: #180032; }
.ac-update__message { font-size: 14px; line-height: 1.6; color: #2a1a40; }
.ac-update__message ::v-deep p { margin-bottom: 4px; }

/* Dialogo */
.ac-dialog { border-radius: 18px !important; }
.ac-dialog__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 22px 24px 6px; }
.ac-dialog__title { margin: 6px 0 0; font-size: 21px; font-weight: 700; color: #25113c; letter-spacing: -.02em; }
.ac-dialog__body { padding: 12px 24px 8px !important; }
.ac-dialog__footer { gap: 8px; padding: 12px 24px 20px !important; border-top: 1px solid rgba(24, 0, 50, .06); }

@media (max-width: 760px) {
  .ac-row { grid-template-columns: auto minmax(0, 1fr); gap: 10px 14px; }
  .ac-pill { grid-column: 2; justify-self: start; }
  .ac-row__actions { grid-column: 1 / -1; justify-content: flex-start; border-top: 1px solid rgba(24, 0, 50, .06); padding-top: 6px; }
}
</style>
